import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CustomerEsimQr } from "@/components/CustomerEsimQr";
import { useSearchParams } from "react-router-dom";
import { Receipt, Signal, Copy, Download, FileText, RefreshCw } from "lucide-react";
import { previewPdf } from "@/lib/pdfPreview";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_LABEL = { sim: "SIM", ricarica: "Ricarica", gift: "Gift Card", esim: "eSIM", servizio: "Servizio", phone: "Smartphone", accessorio: "Accessorio" };
const PERIODS = { all: null, "7": 7, "30": 30, "90": 90 };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function MyPurchases() {
  const [orders, setOrders] = useState([]);
  const [esims, setEsims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [loadError, setLoadError] = useState(false);
  const [params, setParams] = useSearchParams();
  const loadPurchases = useCallback(async () => {
    setLoading(true); setLoadError(false);
    try {
      const [ordersResult, esimsResult] = await Promise.all([api.get("/portal/orders"), api.get("/portal/esims")]);
      setOrders(ordersResult.data || []); setEsims(esimsResult.data || []);
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadPurchases(); }, [loadPurchases]);

  const [billChecks, setBillChecks] = useState({});
  const checkingBills = useRef(false);
  const [checkingBill, setCheckingBill] = useState(null);
  const checkBill = useCallback(async (id) => {
    setCheckingBill(id);
    try {
      const { data } = await api.get(`/portal/orders/${id}/bollettino-status`);
      setBillChecks((old) => ({ ...old, [id]: { ...data, error: null } }));
      if (data.status === "paid") setOrders((old) => old.map((row) => row.id === id ? { ...row, status: "completed" } : row));
    } catch (error) {
      setBillChecks((old) => ({ ...old, [id]: { ...old[id], error: formatApiErrorDetail(error.response?.data?.detail) || "Verifica non riuscita: ultimo stato valido conservato. Non ripetere il pagamento." } }));
    } finally { setCheckingBill(null); }
  }, []);
  const refreshPending = useCallback(async () => {
    if (checkingBills.current) return;
    checkingBills.current = true;
    try { for (const order of orders.filter((o) => o.status === "pending" && o.provider === "openapi_bollettini")) await checkBill(order.id); }
    finally { checkingBills.current = false; }
  }, [orders, checkBill]);
  useEffect(() => {
    if (!orders.some((o) => o.status === "pending" && o.provider === "openapi_bollettini")) return;
    const t = setInterval(refreshPending, 60000);
    return () => clearInterval(t);
  }, [orders, refreshPending]);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiato"); };
  const downloadReceipt = async (id) => {
    try {
      const r = await api.get(`/portal/orders/${id}/receipt`, { responseType: "blob" });
      previewPdf(r.data, `ricevuta-${id.slice(-8)}.pdf`);
    } catch { toast.error("Ricevuta non disponibile"); }
  };

  const cats = Array.from(new Set(orders.map((o) => o.category).filter(Boolean)));
  const withinPeriod = (s) => {
    const days = PERIODS[periodFilter];
    if (!days || !s) return true;
    return (Date.now() - new Date(s).getTime()) <= days * 86400000;
  };
  const filtered = orders.filter((o) => (catFilter === "all" || o.category === catFilter) && withinPeriod(o.created_at));

  return (
    <div className="space-y-6" data-testid="my-purchases-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Area cliente</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">I miei acquisti</h1>
        <p className="text-sm text-muted-foreground mt-1">Storico ordini, stato servizi, ricevute PDF e download delle tue eSIM e gift card.</p>
      </div>

      <Button size="sm" variant="outline" disabled={loading} onClick={loadPurchases} data-testid="purchases-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Aggiorna acquisti</Button>
      {loadError && <p role="alert" data-testid="purchases-error">Impossibile caricare gli acquisti. Premi Aggiorna per riprovare.</p>}
      <Tabs value={params.get("tab") === "esim" ? "esim" : "acquisti"} onValueChange={(value) => setParams((previous) => { const next = new URLSearchParams(previous); next.set("tab", value); return next; }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="acquisti" data-testid="tab-acquisti"><Receipt className="w-4 h-4 mr-1" /> Acquisti</TabsTrigger>
          <TabsTrigger value="esim" data-testid="tab-esim"><Signal className="w-4 h-4 mr-1" /> Le mie eSIM</TabsTrigger>
        </TabsList>

        <TabsContent value="acquisti">
          <div className="flex flex-wrap gap-3 mt-4">
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {cats.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[c] || c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="w-44" data-testid="filter-period"><SelectValue placeholder="Periodo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutto il periodo</SelectItem>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="glow-border mt-4">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Prodotto</TableHead><TableHead>Categoria</TableHead><TableHead>Importo</TableHead><TableHead>Stato</TableHead><TableHead>Data</TableHead><TableHead>Codice</TableHead><TableHead>Ricevuta</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Caricamento…</TableCell></TableRow>}
                {!loading && filtered.map((o) => (
                  <TableRow key={o.id} data-testid={`purchase-row-${o.id}`}>
                    <TableCell className="font-medium">{o.product_name || "—"}</TableCell>
                    <TableCell><Badge variant="outline">{CAT_LABEL[o.category] || o.category}</Badge></TableCell>
                    <TableCell>{euro(o.amount)}</TableCell>
                    <TableCell>
                      <Badge data-testid={`purchase-status-${o.id}`} className={billChecks[o.id]?.error ? "bg-red-600" : o.status === "completed" ? "bg-teal-600 hover:bg-teal-600" : o.status === "error" || o.status === "needs_review" ? "bg-red-500 hover:bg-red-500" : "bg-amber-500 hover:bg-amber-500 text-black"}>{billChecks[o.id]?.error ? "Verifica non riuscita" : billChecks[o.id]?.status === "needs_review" ? "Esito da verificare" : o.status === "completed" ? "Completato" : o.status === "needs_review" ? "Pagamento ricevuto · da verificare" : o.status === "pending" ? (o.category === "esim" ? "Pagamento ricevuto · emissione in corso" : o.provider === "openapi_bollettini" ? "Pagamento non confermato" : "In attesa") : o.status === "error" ? "Errore" : o.status}</Badge>
                      {o.category === "esim" && o.status === "needs_review" && <p className="text-xs mt-1" data-testid={`purchase-review-${o.id}`}>Non pagare di nuovo. Contatta l'assistenza.</p>}
                      {o.provider === "openapi_bollettini" && <div className="max-w-xs text-xs space-y-1 mt-2"><Button size="sm" variant="outline" disabled={checkingBill !== null} onClick={() => checkBill(o.id)} data-testid={`purchase-check-bill-${o.id}`}>Verifica stato</Button>{billChecks[o.id]?.error && <p role="alert" className="text-destructive" data-testid={`purchase-bill-error-${o.id}`}>{billChecks[o.id].error}</p>}{!billChecks[o.id]?.error && billChecks[o.id]?.notice && <p data-testid={`purchase-bill-notice-${o.id}`}>{billChecks[o.id].notice}</p>}{billChecks[o.id]?.error && billChecks[o.id]?.status && <p data-testid={`purchase-bill-last-status-${o.id}`}>Ultimo esito valido conservato: {billChecks[o.id].status === "paid" ? "pagato" : billChecks[o.id].status === "pending" ? "report finale non disponibile" : "da verificare"}.</p>}</div>}
                      {o.payment_url && o.status === "pending" && !billChecks[o.id]?.error && billChecks[o.id]?.status !== "needs_review" && <a href={o.payment_url} target="_blank" rel="noreferrer" className="block text-xs text-primary mt-1" data-testid={`pay-link-${o.id}`}>Paga bollettino →</a>}
                    </TableCell>
                    <TableCell>{fmtDate(o.created_at)}</TableCell>
                    <TableCell>
                      {o.gift_code
                        ? <button className="app-text-button font-mono text-xs text-primary flex items-center gap-1" onClick={() => copy(o.gift_code)} data-testid={`copy-code-${o.id}`}>{o.gift_code} <Copy className="w-3 h-3" /></button>
                        : (o.iccid ? <span className="font-mono text-xs">{o.iccid}</span> : "—")}
                    </TableCell>
                    <TableCell>
                      {o.amount > 0
                        ? <button className="app-text-button text-xs text-primary flex items-center gap-1" onClick={() => downloadReceipt(o.id)} data-testid={`receipt-${o.id}`}><FileText className="w-3.5 h-3.5" /> PDF</button>
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && !loadError && filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessun acquisto per i filtri selezionati.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="esim">
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {loading && <p className="text-muted-foreground">Caricamento…</p>}
            {!loading && esims.map((e) => (
              <Card key={e.id} className="p-5 glow-border" data-testid={`esim-card-${e.id}`}>
                <div className="flex items-center gap-2"><Signal className="w-4 h-4 text-primary" /><span className="font-semibold">{e.package_title}</span></div>
                <div className="flex flex-col sm:flex-row gap-4 mt-4 items-start">
                  {e.private_qr ? <CustomerEsimQr esim={e} /> : <img src={`${BACKEND}${e.qr_url}`} alt="QR eSIM" width={120} height={120} className="rounded-lg bg-white p-2 shrink-0" data-testid={`esim-qr-${e.id}`} />}
                  <div className="text-xs text-muted-foreground space-y-1 break-all">
                    <p>ICCID: <span className="font-mono text-foreground">{e.iccid}</span></p>
                    <p data-testid={`esim-lpa-${e.id}`}>LPA / SM-DP+: <span className="font-mono text-foreground">{e.lpa}</span></p>
                    {e.activation_code && <p data-testid={`esim-activation-${e.id}`}>Codice di attivazione: <span className="font-mono text-foreground">{e.activation_code}</span></p>}
                    <p>{fmtDate(e.created_at)}</p>
                    {e.mock && <p className="text-amber-500">Demo (non attivabile)</p>}
                  </div>
                </div>
                {!e.private_qr && <a href={`${BACKEND}${e.qr_url}`} download target="_blank" rel="noreferrer" className="mt-3 inline-block" data-testid={`esim-download-${e.id}`}>
                  <Button size="sm" variant="outline" className="gap-1"><Download className="w-4 h-4" /> Scarica QR</Button>
                </a>}
              </Card>
            ))}
            {!loading && !loadError && esims.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">Nessuna eSIM associata al tuo account.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
